import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { formatFileSize, getENV, getRaceType } from 'src/app/_helpers/helpers';
import { APIService } from 'src/app/services/api.service';
import { locationMappers } from '../../locations';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'src/app/services/notifier.service';
import * as moment from 'moment';

interface ErrorData {
  HorseName: string;
  Section: string;
  Metric: string;
  Value: number;
  DisplayValue: string;
  OriginalValue?: number;
  Expected: number[];
  RaceLength: number;
  SegmentNumber?: number; // Optional for Thoroughbred
  Status: boolean;
  Label: string;
  animal_id: string; // Add animal_id property
  is_standard: boolean,
  SectionName?: string; // Optional property for section name
}

interface QueryParams {
  venue_name: string;
  race_number: string;
  race_id: string;
  race_date: string;
  external_event_id: string;
  jurisdiction_code: string;
  external_venue_id: string;
  race_length: string;
  pc_number?: string; // optional
}

interface ValueChange {
  originalValue: number;
  newValue: number;
  timestamp: Date;
}

interface PendingChanges {
  [horseId: string]: {
    [metric: string]: {
      [section: string]: ValueChange;
    };
  };
}

interface MetricGroup {
  metricName: string;
  displayName: string;
  errors: ErrorData[];
}

interface HorseGroup {
  horseName: string;
  metrics: MetricGroup[];
}

@Component({
  selector: 'post-race-qa',
  templateUrl: './post-race-qa.component.html',
  styleUrls: ['./post-race-qa.component.css'],
})
export class PostRaceQaComponent implements OnInit {
  @Input() data: any;
  @Output() hideModel = new EventEmitter<boolean>();

  dragging = false;
  file: File | null = null;
  fileError: string | null = null;
  fileSuccess: string | null = null;
  formatFileSize = formatFileSize;

  errorData: ErrorData[] = [];
  loading: boolean = false;
  apiError: string | null = null;

  config: any;
  groupedErrorData: { [key: string]: ErrorData[] } = {};

  filterStatus: 'ALL' | 'PASS' | 'FAIL' = 'ALL';

  showTolUpload = false;
  ignoringRules: boolean = false;
  restoringRules: boolean = false;

  // Add property to track race type
  raceType: string;

  pendingChanges: PendingChanges = {};
  isEditing: boolean = false;
  editingCell: { horse: string; metric: string; section: string } | null = null;

  private readonly SPEED_METRICS = [
    'AverageSpeed',
    'TopSpeed', // Changed from TopSpeedMethod1 to TopSpeed
  ];

  formattedErrorData: HorseGroup[] = [];

  // Update the editingValue property
  editingValue: number | null = null;

  constructor(
    private apiService: APIService,
    private route: ActivatedRoute,
    private router: Router,
    private notifier: NotifierService
  ) {
    this.config = this.route.snapshot.data['config'];
  }

  ngOnInit() {
    if (!this.validateQueryParams()) {
      this.router.navigate(['/dash/home']);
      return;
    }
    this.raceType = getRaceType(
      this.data['event'].external_event_id
    ).raceTypeStr;
    this.data.event.race_type = this.raceType;
    this.fetchErrorData();
  }

  private validateQueryParams(): boolean {
    const params = this.route.snapshot.queryParams;
    const requiredParams: (keyof QueryParams)[] = [
      'venue_name',
      'race_number',
      'race_id',
      'race_date',
      'external_event_id',
      'jurisdiction_code',
      'external_venue_id',
    ];

    const missingParams = requiredParams.filter((param) => !params[param]);

    if (missingParams.length > 0) {
      console.error('Missing required query parameters:', missingParams);
      return false;
    }

    // Structure data object from query params
    this.data = {
      event: {
        venue_name: params['venue_name'],
        date: params['race_date'],
        external_event_id: params['external_event_id'],
        JurisdictionCode: params['jurisdiction_code'],
        pc: params['pc_number'] || null,
        external_venue_id: params['external_venue_id'],
      },
      race: {
        SquentialRaceOrderNumber: params['race_number'],
        ExternalRaceId: params['race_id'],
        race_length: params['race_length'],
      },
    };

    return true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;

    if (!event.dataTransfer?.types.includes('Files')) {
      this.fileError = 'Not a valid file';
      return;
    }

    this.validateFile(event.dataTransfer.files);
  }

  validateFile(files: FileList): boolean {
    this.fileError = null;
    if (!files || files.length === 0) {
      this.fileError = 'Missing file';
      return false;
    }
    if (files.length > 1) {
      this.fileError = 'Multiple files are not allowed';
      return false;
    }

    const file = files[0];
    const validTypes = ['text/xml', 'application/xml'];
    if (!validTypes.includes(file.type)) {
      this.fileError = 'Only XML files are allowed';
      return false;
    }

    this.fileError = '';
    this.file = file;
    return true;
  }

  onFileSelected(event: Event) {
    const element = event.target as HTMLInputElement;
    const files = element.files;
    let status = this.validateFile(files);
    if (!status) {
      element.value = '';
    }
  }

  cancelFile() {
    this.file = null;
    this.fileError = null;
    this.fileSuccess = null;
  }

  refresh() {
    this.fetchErrorData();
  }

  async fetchErrorData() {
    try {
      this.loading = true;
      this.apiError = null;
      this.is_standard = false;

      this.errorData = [];

      const payload = {
        action: 'get_business_rules_error_json',
        location:
          locationMappers[this.data['event'].venue_name] ||
          this.data['event'].venue_name,
        race_number: this.data['race'].SquentialRaceOrderNumber,
        race_id: this.data['race'].ExternalRaceId,
        race_date: this.data['event']['date'],
        type: getRaceType(this.data['event']['external_venue_id'])[
          'raceTypeStr'
        ],
        event_id: this.data['event']['external_event_id'],
        jurisdiction: this.data['event']['JurisdictionCode'],
        pc_number: this.data['event']['pc'],
        venue_id: this.data['event']['external_venue_id'],
      };

      let result = await this.apiService.postDataPromis(
        `${this.config[getENV()].raceAPI}/flask-operations`,
        payload
      );

      if (result.status === '1') {
        // Map the errors array based on race type
        if (result.errors.length > 0) {
          let averageSpeedLabels = new Map<string, string>();
          result.errors
            .filter((error: ErrorData) => error.Metric == 'AverageSpeed')
            .forEach((error: ErrorData) => {
              if (!averageSpeedLabels.has(error.Section)) {
                averageSpeedLabels.set(error.Section, error.Label);
              }
            });
          this.errorData = result.errors
            .filter((error: ErrorData) => error.Metric !== 'TopSpeedMethod2') // Filter out TopSpeedMethod2
            .map((error: ErrorData) => {
              // Convert TopSpeedMethod1 to TopSpeed
              if (error.Metric === 'TopSpeedMethod1') {
                error.Metric = 'TopSpeed';
              }

              const is_standard = error.is_standard != undefined ? error.is_standard : true;

              if(is_standard) {
                this.is_standard = is_standard;
              }

              return {
                HorseName: error.HorseName,
                Section: error.Section,
                Metric: error.Metric,
                Label: averageSpeedLabels.get(error.Section) || error.Label,
                Value: this.isSpeedMetric(error.Metric)
                  ? this.convertMsToKmh(error.Value)
                  : error.Value,
                DisplayValue: this.isSpeedMetric(error.Metric)
                  ? `${this.convertMsToKmh(error.Value).toFixed(2)} km/h`
                  : error.Value.toFixed(2),
                OriginalValue: this.isSpeedMetric(error.Metric)
                  ? this.convertMsToKmh(error.Value)
                  : error.Value,
                Expected: error.Expected.map((val) => {
                  return this.isSpeedMetric(error.Metric)
                    ? this.convertMsToKmh(val)
                    : val;
                }),
                RaceLength: error.RaceLength,
                is_standard: error.is_standard,
                Status: error.Status,
                animal_id: error.animal_id,
                SectionName: error.SectionName || error.Section,
                // Only include SegmentNumber if it exists (for non-Thoroughbred races)
                ...(error.SegmentNumber !== undefined && {
                  SegmentNumber: error.SegmentNumber,
                }),
              };
            });

          // Group errors by horse name
          this.groupedErrorData = this.errorData.reduce((acc, error) => {
            if (!acc[error.HorseName]) {
              acc[error.HorseName] = [];
            }
            acc[error.HorseName].push(error);
            return acc;
          }, {} as { [key: string]: ErrorData[] });
        }
        // After setting errorData
        this.formatErrorData();
      } else {
        this.apiError = result.error_message || 'Failed to fetch data';
      }

      this.loading = false;
    } catch (error) {
      this.apiError = 'An error occurred while fetching data';
    } finally {
      this.loading = false;
    }
  }

  backToReports() {
    const params = this.route.snapshot.queryParams;

    // Format date as "DD MMMM YYYY"
    const formattedDate = moment(params['race_date']).format('DD MMMM YYYY');

    this.router.navigate(['/dash/post-race/race-reports'], {
      queryParams: {
        event_id: params['external_event_id'],
        date: formattedDate,
      },
    });
  }

  is_standard: boolean = false;
  async ignoreBussinessRules() {
    try {
      this.ignoringRules = true;
      this.apiError = null;

      const payload = {
        action: 'result_processor_trigger',
        location:
          locationMappers[this.data['event'].venue_name] ||
          this.data['event'].venue_name,
        type: this.data['event']['race_type'],
        jurisdiction: this.data['event'].JurisdictionCode,
        race_number: this.data['race'].SquentialRaceOrderNumber,
        race_date: this.data['event'].date,
      };

      const result = await this.apiService.postDataPromis(
        `${this.config[getENV()].raceAPI}/flask-operations`,
        payload
      );

      if (result.status === '1') {
        this.notifier.alert(
          'Success',
          '',
          'Business rules ignored successfully',
          'success'
        );
        await this.fetchErrorData(); // Refresh data
      } else {
        this.apiError =
          result.error_message || 'Failed to ignore business rules';
        this.notifier.alert('Error', '', this.apiError, 'error');
      }
    } catch (error) {
      this.apiError = error.message;
      this.notifier.alert('Error', '', this.apiError, 'error');
    } finally {
      this.ignoringRules = false;
    }
  }

  public isSpeedMetric(metric: string): boolean {
    return this.SPEED_METRICS.includes(metric);
  }

  public convertMsToKmh(speedMs: number): number {
    return Number(speedMs * 3.6);
  }

  public convertKmhToMs(speedKmh: number): number {
    return Number(speedKmh / 3.6);
  }

  public formatMetricName(metric: string): string {
    return metric
      .split(/(?=[A-Z0-9])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  public getDisplayValue(value: number, metric: string): string {
    if (!value) return '0.00';
    if (this.isSpeedMetric(metric)) {
      return `${this.convertMsToKmh(value).toFixed(2)} km/h`;
    }
    return value.toFixed(2);
  }

  public getExpectedRange(expected: (number | null)[], metric: string): string {
    if (!expected || !Array.isArray(expected) || expected.length !== 2) {
      return 'N/A';
    }

    const [min, max] = expected;
    const unit = this.isSpeedMetric(metric) ? ' km/h' : '';

    if (min === null && max === null) return 'No Range';
    if (min === null) {
      const maxValue = max;
      return `Up to ${maxValue.toFixed(2)}${unit}`;
    }
    if (max === null) {
      const minValue = min;
      return `From ${minValue.toFixed(2)}${unit}`;
    }

    const minValue = min;
    const maxValue = max;
    return `${minValue.toFixed(2)} - ${maxValue.toFixed(2)}${unit}`;
  }

  public calculateDeviation(
    value: number,
    expected: number[],
    metric: string
  ): { type: string; value: string } {
    // Return early if both expected values are null
    if (
      !expected ||
      expected.length !== 2 ||
      (expected[0] === null && expected[1] === null)
    ) {
      return { type: 'neutral', value: '' }; // Empty string instead of 'N/A'
    }

    const unit = this.isSpeedMetric(metric) ? ' km/h' : '';
    const convertedValue = value;
    const [min, max] = expected.map((val) => (val === null ? val : val));

    if (min !== null && convertedValue < min) {
      return {
        type: 'low',
        value: `-${(min - convertedValue).toFixed(2)}${unit}`,
      };
    }
    if (max !== null && convertedValue > max) {
      return {
        type: 'high',
        value: `+${(convertedValue - max).toFixed(2)}${unit}`,
      };
    }
    return { type: 'neutral', value: '0.00' };
  }

  get totalChecks(): number {
    return this.errorData.length;
  }

  get passedChecks(): number {
    return this.errorData.filter((e) => e.Status).length;
  }

  get failedChecks(): number {
    return this.errorData.filter((e) => !e.Status).length;
  }

  get filteredGroupedErrorData(): { [key: string]: ErrorData[] } {
    if (this.filterStatus === 'ALL') {
      return this.groupedErrorData;
    }

    const filtered = {};
    Object.keys(this.groupedErrorData).forEach((horseName) => {
      const filteredErrors = this.groupedErrorData[horseName].filter((error) =>
        this.filterStatus === 'PASS' ? error.Status : !error.Status
      );

      if (filteredErrors.length > 0) {
        filtered[horseName] = filteredErrors;
      }
    });

    return filtered;
  }

  get doubleGroupedErrorData(): {
    [horseName: string]: { [metric: string]: ErrorData[] };
  } {
    const grouped: { [horseName: string]: { [metric: string]: ErrorData[] } } =
      {};

    Object.entries(this.filteredGroupedErrorData).forEach(
      ([horseName, errors]) => {
        grouped[horseName] = {};

        errors.forEach((error) => {
          if (!grouped[horseName][error.Metric]) {
            grouped[horseName][error.Metric] = [];
          }
          grouped[horseName][error.Metric].push(error);
        });
      }
    );

    return grouped;
  }

  getMetricRowCount(horseMetrics: { [metric: string]: ErrorData[] }): number {
    return Object.values(horseMetrics).reduce(
      (total, errors) => total + errors.length,
      0
    );
  }

  getMetricLength(errors: ErrorData[]): number {
    return errors?.length || 0;
  }

  setFilter(status: 'ALL' | 'PASS' | 'FAIL') {
    this.filterStatus = status;
    this.formattedErrorData = []; // Clear existing data
    this.formatErrorData(); // Reformat with new filter
  }

  openTolUpload() {
    this.showTolUpload = true;
  }

  closeTolUpload() {
    this.showTolUpload = false;
  }

  public startEdit(error: ErrorData): void {
    this.editingCell = {
      horse: error.HorseName,
      metric: error.Metric,
      section: error.Section,
    };

    // Set the initial editing value
    this.editingValue = error.Value;

    if (!this.pendingChanges[error.HorseName]) {
      this.pendingChanges[error.HorseName] = {};
    }
    if (!this.pendingChanges[error.HorseName][error.Metric]) {
      this.pendingChanges[error.HorseName][error.Metric] = {};
    }
  }

  // Fix updateValue method to handle undefined pendingChanges
  updateValue(horse: string, metric: string, section: string, value: string) {
    if (!this.pendingChanges[horse]) {
      this.pendingChanges[horse] = {};
    }
    if (!this.pendingChanges[horse][metric]) {
      this.pendingChanges[horse][metric] = {};
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      // Convert km/h to m/s for speed metrics before storing
      const finalValue = this.isSpeedMetric(metric)
        ? this.convertKmhToMs(numValue)
        : numValue;

      if (!this.pendingChanges[horse][metric][section]) {
        // Initialize if not exists
        this.pendingChanges[horse][metric][section] = {
          originalValue: this.isSpeedMetric(metric)
            ? this.convertKmhToMs(numValue)
            : numValue,
          newValue: finalValue,
          timestamp: new Date(),
        };
      } else {
        // Update existing value
        this.pendingChanges[horse][metric][section].newValue = Number(
          finalValue.toFixed(2)
        );
      }
    }
  }

  confirmEdit(error: ErrorData) {
    error.Value = this.editingValue;

    error.DisplayValue = this.isSpeedMetric(error.Metric)
      ? `${this.editingValue.toFixed(2)} km/h`
      : error.Value.toFixed(2);

    let deviation = this.calculateDeviation(
      error.Value,
      error.Expected,
      error.Metric
    );

    if (deviation.type == 'neutral') {
      error.Status = true;
    } else {
      error.Status = false;
    }

    this.editingCell = null;
  }

  cancelEdit() {
    this.pendingChanges = {};
    this.editingCell = null;
    this.editingValue = null;
  }

  saving: boolean = false;
  async saveChanges() {
    try {
      this.saving = true;
      const changes = [];

      this.formattedErrorData.forEach((horse) => {
        horse.metrics.forEach((metric) => {
          metric.errors.forEach((error) => {
            if (error.Value !== error.OriginalValue) {
              // If metric is TopSpeed, convert it back to TopSpeedMethod1 for API compatibility
              let apiMetric = error.Metric;
              switch(error.Metric) {
                case 'TopSpeed':
                  apiMetric = 'TopSpeedMethod1';
                  break;
                case 'StrideFrequency':
                  apiMetric = 'AverageStrideFrequency';
                  break;
                case 'StrideLength':
                  apiMetric = 'AverageStrideLength';
                  break;
                default:
                  apiMetric = error.Metric;
              }

              const change = {
                HorseName: error.HorseName,
                Section: error.Section,
                Metric: apiMetric,
                Value: this.isSpeedMetric(error.Metric)
                  ? this.convertKmhToMs(error.Value)
                  : error.Value,
                Expected: error.Expected.map((val: number) => {
                  return this.isSpeedMetric(error.Metric)
                    ? this.convertKmhToMs(val)
                    : val;
                }),
                RaceLength: error.RaceLength,
                Status: error.Status,
                AnimalID: error.animal_id, // Include animal_id in changes
              };

              if(apiMetric === 'TopSpeedMethod1') {
                changes.push({
                  ...change,
                  Metric: 'TopSpeedMethod2',
                });
              }

              error.OriginalValue = error.Value;
              changes.push(change);
            }
          });
        });
      });

      if (changes.length === 0) {
        this.saving = false;
        this.notifier.alert('Info', '', 'No changes to save', 'info', 5000);
        return;
      }

      const payload = {
        action: 'update_business_rules_values',
        changes: changes,
        race_id: this.data.race.ExternalRaceId,
        race_date: this.data.event.date,
        race_number: this.data.race.SquentialRaceOrderNumber,
        event_id: this.data.event.external_event_id,
        venue_name: this.data.event.venue_name,
        type: getRaceType(this.data.event.external_venue_id)['raceTypeStr'],
        jurisdiction_code: this.data.event.JurisdictionCode,
        external_venue_id: this.data.event.external_venue_id,
      };

      const result = await this.apiService.postDataPromis(
        `${this.config[getENV()].raceAPI}/flask-operations`,
        payload
      );

      if (result.status === '1') {
        this.notifier.alert(
          'Success',
          '',
          'Changes saved successfully',
          'success'
        );
        await this.fetchErrorData();
      } else {
        this.apiError = result.error_message || 'Failed to save changes';
        this.notifier.alert('Error', '', this.apiError, 'error');
      }
    } catch (error) {
      this.apiError = error.message;
      this.notifier.alert('Error', '', this.apiError, 'error');
    }
    this.saving = false;
  }

  hasChanges(): boolean {
    return Object.keys(this.pendingChanges).length > 0;
  }

  // Add this method to format the data
  private formatErrorData(): void {
    this.formattedErrorData = []; // Clear existing data

    Object.entries(this.filteredGroupedErrorData).forEach(
      ([horseName, errors]) => {
        const metricGroups = new Map<string, ErrorData[]>();

        errors.forEach((error) => {
          if (!metricGroups.has(error.Metric)) {
            metricGroups.set(error.Metric, []);
          }

          // Transform section labels for AverageSpeed
          if (error.Metric === 'AverageSpeed') {
            const transformedError = { ...error };
            // Reverse the segment numbering (4 -> 0, 3 -> 1, etc)
            transformedError.Section = error.Section;
            metricGroups.get(error.Metric).push(transformedError);
          } else {
            metricGroups.get(error.Metric).push(error);
          }
        });

        const metrics: MetricGroup[] = Array.from(metricGroups.entries()).map(
          ([metric, metricErrors]) => ({
            metricName: metric,
            displayName: this.formatMetricName(metric),
            errors: metricErrors,
          })
        );
        console.log(metrics)
        this.formattedErrorData.push({
          horseName,
          metrics,
        });
      }
    );
  }

  async restoreBusinessRules() {
    try {
      this.restoringRules = true;

      const payload = {
        action: 'restore_business_rules',
        venue_name: this.data['event'].venue_name,
        race_number: this.data['race'].SquentialRaceOrderNumber,
        race_date: this.data['event']['date'],
        jurisdiction_code: this.data['event'].JurisdictionCode,
        type: getRaceType(this.data['event']['external_venue_id'])['raceTypeStr']
      };

      const result = await this.apiService.postDataPromis(
        `${this.config[getENV()].raceAPI}/flask-operations`,
        payload
      );

      if (result.status === '1') {
        this.notifier.alert(
          'Success',
          '',
          'Business rules restored successfully',
          'success'
        );
        await this.fetchErrorData(); // Refresh data
      } else {
        this.notifier.alert('Error', '', this.apiError, 'error');
      }
    } catch (error) {
      this.notifier.alert('Error', '', this.apiError, 'error');
    } finally {
      this.restoringRules = false;
    }
  }

  async downloadTOLXML() {
    this.notifier.loading(true);
    let payload: any = {
      action: 'get_tol_presigned_url_download',
      race_id: this.data.race.ExternalRaceId,
      race_date: this.data.event.date,
      race_number: this.data.race.SquentialRaceOrderNumber,
      event_id: this.data.event.external_event_id,
      venue_name: this.data.event.venue_name,
      type: getRaceType(this.data.event.external_venue_id)['raceTypeStr'],
      jurisdiction_code: this.data.event.JurisdictionCode,
      external_venue_id: this.data.event.external_venue_id,
    }

    if (this.data.event.pc) {
      payload.pc_number = this.data.event.pc;
    }
    let apiURL:string = `${this.config[getENV()].raceAPI}/flask-operations`;
    let result: any = await this.apiService.postDataPromis(
      apiURL,
      payload
    );
    if (result.status === '1') {
      const downloadUrl = result.presign_url;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      this.notifier.alert('Info', '', result.error_message || 'Failed to Download TOL XML File', 'info');
    }

    this.notifier.loading(false);
  }

  // Update value change handler
  onValueChange(): void {
    if (!this.editingCell || this.editingValue === null) return;

    const { horse, metric, section } = this.editingCell;

    if (!this.pendingChanges[horse][metric][section]) {
      this.pendingChanges[horse][metric][section] = {
        originalValue: this.editingValue,
        newValue: this.isSpeedMetric(metric)
          ? this.convertKmhToMs(this.editingValue)
          : this.editingValue,
        timestamp: new Date(),
      };
    } else {
      this.pendingChanges[horse][metric][section].newValue = this.isSpeedMetric(
        metric
      )
        ? this.convertKmhToMs(this.editingValue)
        : this.editingValue;
    }
  }
}
